import {useEffect, useState} from 'react';

// AD-2705: the bars at the top of the form editor stay in place with CSS position sticky, which the browser draws
// with the page, so nothing flickers or jumps. These two CSS variables give their heights to anything below them:
// --app-header-height is the Admicity logo bar, and --sticky-bar-height is the editor's tab bar.
const setHeightVariable = (name, el) => {
    document.documentElement.style.setProperty(name, `${el ? Math.round(el.getBoundingClientRect().height) : 0}px`);
};

// Keeps the logo bar's height in --app-header-height, for anything that stays in view below it.
export const useAppHeaderHeight = (enabled = true) => {
    useEffect(() => {
        if (!enabled) return undefined;
        const header = document.querySelector('header');
        const measure = () => setHeightVariable('--app-header-height', header);
        measure();
        const resizeObserver = typeof ResizeObserver === 'undefined' || !header ? null : new ResizeObserver(measure);
        resizeObserver?.observe(header);
        window.addEventListener('resize', measure);
        return () => {
            resizeObserver?.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [enabled]);
};

// Keeps the logo bar's height in --app-header-height and the bar's own height in --sticky-bar-height. The bar uses
// them with position sticky (AdmicityTabs). Nothing moves on scroll; the scroll listener only reads the bar's position,
// to show a shadow under it while it is following the page.
const useStickyBarOffset = (ref, enabled = true) => {
    const [isFollowing, setIsFollowing] = useState(false);

    useEffect(() => {
        if (!enabled) {
            setIsFollowing(false);
            return undefined;
        }

        const header = document.querySelector('header');
        const bar = ref.current;

        const measure = () => {
            setHeightVariable('--app-header-height', header);
            setHeightVariable('--sticky-bar-height', ref.current);
        };
        measure();

        const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
        if (resizeObserver) {
            if (header) resizeObserver.observe(header);
            if (bar) resizeObserver.observe(bar);
        }

        const updateShadow = () => {
            const current = ref.current;
            if (!current) return;
            const headerBottom = header ? header.getBoundingClientRect().bottom : 0;
            setIsFollowing(window.scrollY > 0 && current.getBoundingClientRect().top <= headerBottom + 1);
        };
        updateShadow();
        window.addEventListener('scroll', updateShadow, {passive: true});
        window.addEventListener('resize', measure);

        return () => {
            resizeObserver?.disconnect();
            window.removeEventListener('scroll', updateShadow);
            window.removeEventListener('resize', measure);
            document.documentElement.style.setProperty('--sticky-bar-height', '0px');
        };
    }, [enabled, ref]);

    return isFollowing;
};

export default useStickyBarOffset;
